import * as THREE from 'three';

// Kit recolor (D34/D65d → D66): the Meshy texture bakes a two-ink striped kit — a saturated TEAL ink
// (hue ~180°, verified by histogram over the map: sat ≈ 0.90) and a bright near-neutral WHITE ink
// (sat ≈ 0.04) — over fixed skin/hair/boots. At rig spawn we patch the material shader (onBeforeCompile)
// so each ink region is repainted INDEPENDENTLY toward a target colour; saturation/luminance folds
// survive because only hue/sat swap and value is a scale. Skin/hair/boots are outside both masks and
// untouched. The JS below is the unit-tested mirror of the GLSL patch (same constants — keep in sync).
//
// Each ink gets a SlotPaint { hex, mix, valScale }:
//   mix       0 = keep the texel's own hue/sat (identity), 1 = full hue+sat swap toward hex, between = tint.
//   valScale  multiply the region's luminance (equalise a bright ink to a darker one; the GK uses 1.5).
// Three kit styles then fall out as pure data (see whiteStripesKit / twoColorKit / unicolorKit):
//   1. coloured + white stripes   teal→colour,  white→identity (or a subtle tint)
//   2. two-coloured stripes       teal→A,        white→B (value-matched so B reads as a true colour)
//   3. unicolor (faintly shaded)  teal→A,        white→A (value-matched so the stripes fade into one)
//
// KNOWN GAP (Tier 1, HSV masks): whiteMask also fires on the character's cartoon eye-whites and teeth.
// Harmless for style 1 / subtle tints; a FULL saturated white repaint (styles 2/3 with a vivid colour)
// tints the eyes/teeth. Fine at gameplay distance; for a close-up hero kit, bake a region-ID map (Tier 2).

export const TEAL_HUE_CENTER = 180 / 360; // 0.5 — the baked dress teal
export const TEAL_HUE_HALF_WIDTH = 0.085; // accept hue ∈ [~150°, ~210°] (the kit's teal trim lands ~160°)
export const TEAL_SAT_MIN = 0.25; // below this it's fabric-in-shadow / white — not the teal ink
// The WHITE ink: low-sat, bright.
export const WHITE_SAT_MAX = 0.22; // sat below this (and bright) = the white ink
export const WHITE_VAL_MIN = 0.55;
// Pull the bright white ink down to the teal ink's luminance so a repainted white region reads as the
// SAME strength colour as a repainted teal region. This is the ratio of the two inks' MEAN VALUE measured
// in LINEAR shader space (teal ≈ 0.601, white ≈ 0.986 → 0.61) — NOT the sRGB-byte ratio (~0.80).
export const WHITE_TO_TEAL_VALUE_MATCH = 0.61;
// Value ceiling — a guard on valScale (the GK uses 1.5); saturated hues above 1.0 blow out to white by
// design (the keeper glow), so this stays ≥ 1.5 and only caps a runaway scale.
export const KIT_VAL_MAX = 2.0;
const TAU = Math.PI * 2;

// ── the paint model ──
export interface SlotPaint {
  /** Target colour for this ink (linearised internally). The SOURCE region is baked-fixed by the texture. */
  hex: number;
  /** 0 = keep the texel's hue/sat, 1 = full swap toward `hex`, between = tint. */
  mix: number;
  /** Multiply this region's luminance (1 = keep the baked shading). Ignored when `flatV` is set. */
  valScale: number;
  /** SOLID output value for the region (0..1): drops the baked stripe/fold shading so the region reads as
   *  ONE flat colour (scene lighting still gives 3-D form). Omit to keep the shaded `texel × valScale`. */
  flatV?: number;
}
/** teal = the saturated ink, white = the near-neutral ink. Both repaint independently. */
export interface KitPaint {
  teal: SlotPaint;
  white: SlotPaint;
}

/** A full hue+sat swap toward `hex` (mix 1), optionally scaling the region's luminance (keeps shading). */
export function swapSlot(hex: number, valScale = 1, mix = 1): SlotPaint {
  return { hex, mix, valScale };
}
/** A SOLID slot: hue/sat of `hex`, a FLAT value (`flatV`) — no baked stripe/fold shading. */
export function solidSlot(hex: number, flatV: number, mix = 1): SlotPaint {
  return { hex, mix, valScale: 1, flatV };
}
/** Leave the region exactly as baked. */
export const IDENTITY_SLOT: SlotPaint = { hex: 0xffffff, mix: 0, valScale: 1 };
/** Flat value for a solid, stripe-free unicolor jersey — a mid value; scene lighting supplies the 3-D form. */
export const SOLID_JERSEY_V = 0.72;

// ── style presets (the three requested looks, as pure data) ──
/** Style 1: coloured stripes on white. `tintHex`=null keeps the stripes white; a hex tints them by `tintMix`. */
export function whiteStripesKit(primary: number, tintHex: number | null = null, tintMix = 1): KitPaint {
  return { teal: swapSlot(primary), white: tintHex === null ? IDENTITY_SLOT : swapSlot(tintHex, 1, tintMix) };
}
/** Style 2: two full colours — `primary` on the teal ink, `secondary` on the white ink (value-matched). */
export function twoColorKit(primary: number, secondary: number): KitPaint {
  return { teal: swapSlot(primary), white: swapSlot(secondary, WHITE_TO_TEAL_VALUE_MATCH) };
}
/** Style 3: one SOLID colour across both inks — a flat value drops the stripe pattern entirely (scene
 *  lighting shades the form). `flatV` sets the brightness (e.g. brighter for a neon keeper). */
export function unicolorKit(color: number, flatV = SOLID_JERSEY_V): KitPaint {
  return { teal: solidSlot(color, flatV), white: solidSlot(color, flatV) };
}

// ── colour maths (pure; JS mirror of the GLSL helpers) ──
export function rgbToHsv(r: number, g: number, b: number): [number, number, number] {
  const mx = Math.max(r, g, b);
  const mn = Math.min(r, g, b);
  const d = mx - mn;
  const s = mx === 0 ? 0 : d / mx;
  let h = 0;
  if (d > 0) {
    if (mx === r) h = ((g - b) / d) % 6;
    else if (mx === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
    if (h < 0) h += 1;
  }
  return [h, s, mx];
}

export function hsvToRgb(h: number, s: number, v: number): [number, number, number] {
  const i = Math.floor(h * 6);
  const f = h * 6 - i;
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const t = v * (1 - (1 - f) * s);
  switch (((i % 6) + 6) % 6) {
    case 0: return [v, t, p];
    case 1: return [q, v, p];
    case 2: return [p, v, t];
    case 3: return [p, q, v];
    case 4: return [t, p, v];
    default: return [v, p, q];
  }
}

const smoothstep = (e0: number, e1: number, x: number): number => {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
};
const fract = (x: number): number => x - Math.floor(x);
const clamp = (x: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, x));

/** 0..1 — how "teal ink" is this texel, given its hue/sat? (soft edges so anti-aliasing doesn't speckle) */
function tealMaskHsv(h: number, s: number): number {
  const hueNear = 1 - smoothstep(TEAL_HUE_HALF_WIDTH * 0.47, TEAL_HUE_HALF_WIDTH, Math.abs(h - TEAL_HUE_CENTER));
  const satGate = smoothstep(TEAL_SAT_MIN, TEAL_SAT_MIN + 0.15, s);
  return hueNear * satGate;
}
/** 0..1 — how "white ink" is this texel, given its sat/value? (the RAW detector, before the teal partition) */
function whiteMaskHsv(s: number, v: number): number {
  const satGate = 1 - smoothstep(WHITE_SAT_MAX, WHITE_SAT_MAX + 0.12, s); // 1 when very unsaturated
  const valGate = smoothstep(WHITE_VAL_MIN, WHITE_VAL_MIN + 0.15, v); // 1 when bright
  return satGate * valGate;
}

/** 0..1 — how "teal ink" is this texel? */
export function tealMask(r: number, g: number, b: number): number {
  const [h, s] = rgbToHsv(r, g, b);
  return tealMaskHsv(h, s);
}
/** 0..1 — the raw white-ink detector (BEFORE the teal partition; recolorKit subtracts the teal share). */
export function whiteMask(r: number, g: number, b: number): number {
  const [, s, v] = rgbToHsv(r, g, b);
  return whiteMaskHsv(s, v);
}

/** D92 — 0..1 KIT coverage: how much this texel is jersey ink (teal ∪ white, partitioned exactly as
 *  recolorKit does), gated by `headCut`. The JS mirror of the shader's `fafiKitMask`: the emissive
 *  highlight is multiplied by it, so a glow lights ONLY jersey pixels — never skin, hair or boots. */
export function kitInkCoverage(r: number, g: number, b: number, headCut = 1): number {
  const [h, s, v] = rgbToHsv(r, g, b);
  const tealRaw = tealMaskHsv(h, s);
  const mA = tealRaw * headCut;
  const mB = whiteMaskHsv(s, v) * (1 - tealRaw) * headCut;
  return clamp(mA + mB, 0, 1);
}

/** sRGB byte → linear (three.js works in linear space; texture texels arrive linear in-shader). */
export function srgbByteToLinear(b: number): number {
  const c = b / 255;
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

/** A slot's target hue/sat in LINEAR space (THREE.Color.setHex linearises — do it explicitly). */
function slotHsvLinear(hex: number): [number, number] {
  const [h, s] = rgbToHsv(srgbByteToLinear((hex >> 16) & 255), srgbByteToLinear((hex >> 8) & 255), srgbByteToLinear(hex & 255));
  return [h, s];
}

/** Recolor one texel (linear rgb in/out) per the two-ink KitPaint. The EXACT JS mirror of the GLSL below.
 *  Compose is convex and order-independent: the white ink is partitioned off the teal ink so a soft-edge
 *  texel where both masks fire is never double-painted or double-scaled; hue takes the shortest arc.
 *  `headCut` (0..1, in-shader `step(localY, uChinY)`) zeroes BOTH masks above the chin so the face —
 *  eye-whites and teeth in particular — is never repainted; 1 = recolor normally (default / below chin). */
export function recolorKit(r: number, g: number, b: number, kit: KitPaint, headCut = 1): [number, number, number] {
  const [h, s, v] = rgbToHsv(r, g, b);
  const tealRaw = tealMaskHsv(h, s);
  const mA = tealRaw * headCut; // above the chin (headCut 0) nothing recolors — skin/hair/eyes/teeth stay baked
  const mB = whiteMaskHsv(s, v) * (1 - tealRaw) * headCut; // partition off teal, then exclude the head
  const [hA, sA] = slotHsvLinear(kit.teal.hex);
  const [hB, sB] = slotHsvLinear(kit.white.hex);
  const aA = mA * kit.teal.mix; // hue/sat pull toward each slot
  const aB = mB * kit.white.mix;
  // hue: shortest-arc lerp toward the (aA,aB)-weighted circular mean of the two targets (exact when one fires)
  const hx = aA * Math.cos(TAU * hA) + aB * Math.cos(TAU * hB);
  const hy = aA * Math.sin(TAU * hA) + aB * Math.sin(TAU * hB);
  const hTarget = hx * hx + hy * hy > 1e-6 ? fract(Math.atan2(hy, hx) / TAU) : h;
  let dH = hTarget - h;
  dH -= Math.floor(dH + 0.5); // shortest way round the wheel (matches GLSL floor(dH + 0.5))
  const hEff = fract(h + dH * Math.min(1, aA + aB));
  const sEff = clamp((1 - aA - aB) * s + aA * sA + aB * sB, 0, 1);
  // value: each ink emits a FLAT `flatV` (solid) or its shaded `texel × valScale`; convex blend with the
  // untouched texel value for non-ink texels (skin/head/soft edges) → no overshoot.
  const vTeal = kit.teal.flatV ?? v * kit.teal.valScale;
  const vWhite = kit.white.flatV ?? v * kit.white.valScale;
  const vEff = clamp(mA * vTeal + mB * vWhite + (1 - mA - mB) * v, 0, KIT_VAL_MAX);
  return hsvToRgb(hEff, sEff, vEff);
}

// ── GLSL mirror (same constants/expressions as above — keep in sync) ──
// Vertex: carry the BIND-POSE local height to the fragment. It is the raw `position` attribute (pre-skin,
// pre-scale), so a head vertex reads "high" in every pose — the head-cut survives ragdolls/slides/dives.
const RECOLOR_GLSL_VERT_COMMON = /* glsl */ `
#include <common>
varying float vFafiLocalY;
`;
const RECOLOR_GLSL_VERT_BODY = /* glsl */ `
#include <begin_vertex>
vFafiLocalY = position.y;
`;
const RECOLOR_GLSL_COMMON = /* glsl */ `
#include <common>
uniform vec4 uInkTeal;  // xy = target hue/sat (linear), z = mix, w = valScale
uniform vec4 uInkWhite; // xy = target hue/sat (linear), z = mix, w = valScale
uniform vec2 uFlatV;    // per-ink SOLID value (x = teal, y = white); < 0 = shaded (texel × valScale)
uniform float uChinY;   // recolor only where local Y <= this (below the chin); the face is never repainted
varying float vFafiLocalY;
// D92: how much this texel belongs to the KIT (teal ink + white ink, already head-cut) — written by the
// map-stage recolor, read by the emissive stage so the glow lights ONLY jersey pixels, never skin/boots.
float fafiKitMask = 0.0;
vec3 fafiRgb2hsv(vec3 c) {
  float mx = max(c.r, max(c.g, c.b));
  float mn = min(c.r, min(c.g, c.b));
  float d = mx - mn;
  float s = mx == 0.0 ? 0.0 : d / mx;
  float h = 0.0;
  if (d > 0.0) {
    if (mx == c.r) h = mod((c.g - c.b) / d, 6.0);
    else if (mx == c.g) h = (c.b - c.r) / d + 2.0;
    else h = (c.r - c.g) / d + 4.0;
    h /= 6.0;
    if (h < 0.0) h += 1.0;
  }
  return vec3(h, s, mx);
}
vec3 fafiHsv2rgb(vec3 c) {
  float h = c.x * 6.0;
  float f = fract(h);
  float p = c.z * (1.0 - c.y);
  float q = c.z * (1.0 - f * c.y);
  float t = c.z * (1.0 - (1.0 - f) * c.y);
  int i = int(mod(floor(h), 6.0));
  if (i == 0) return vec3(c.z, t, p);
  if (i == 1) return vec3(q, c.z, p);
  if (i == 2) return vec3(p, c.z, t);
  if (i == 3) return vec3(p, q, c.z);
  if (i == 4) return vec3(t, p, c.z);
  return vec3(c.z, p, q);
}
`;

const RECOLOR_GLSL_MAP = /* glsl */ `
#include <map_fragment>
{
  vec3 hsv = fafiRgb2hsv(diffuseColor.rgb);
  float headCut = step(vFafiLocalY, uChinY); // 1 below the chin, 0 above → the face never recolors
  float hueNear = 1.0 - smoothstep(${(TEAL_HUE_HALF_WIDTH * 0.47).toFixed(4)}, ${TEAL_HUE_HALF_WIDTH.toFixed(4)}, abs(hsv.x - ${TEAL_HUE_CENTER.toFixed(4)}));
  float tealRaw = hueNear * smoothstep(${TEAL_SAT_MIN.toFixed(4)}, ${(TEAL_SAT_MIN + 0.15).toFixed(4)}, hsv.y);
  float wSat = 1.0 - smoothstep(${WHITE_SAT_MAX.toFixed(4)}, ${(WHITE_SAT_MAX + 0.12).toFixed(4)}, hsv.y);
  float wVal = smoothstep(${WHITE_VAL_MIN.toFixed(4)}, ${(WHITE_VAL_MIN + 0.15).toFixed(4)}, hsv.z);
  float mA = tealRaw * headCut;
  float mB = wSat * wVal * (1.0 - tealRaw) * headCut;
  float aA = mA * uInkTeal.z;
  float aB = mB * uInkWhite.z;
  vec2 hv = aA * vec2(cos(${TAU.toFixed(7)} * uInkTeal.x), sin(${TAU.toFixed(7)} * uInkTeal.x))
          + aB * vec2(cos(${TAU.toFixed(7)} * uInkWhite.x), sin(${TAU.toFixed(7)} * uInkWhite.x));
  float hTarget = dot(hv, hv) > 1e-6 ? fract(atan(hv.y, hv.x) / ${TAU.toFixed(7)}) : hsv.x;
  float dH = hTarget - hsv.x;
  dH -= floor(dH + 0.5);
  float hEff = fract(hsv.x + dH * min(1.0, aA + aB));
  float sEff = clamp((1.0 - aA - aB) * hsv.y + aA * uInkTeal.y + aB * uInkWhite.y, 0.0, 1.0);
  float vTeal = uFlatV.x >= 0.0 ? uFlatV.x : hsv.z * uInkTeal.w;
  float vWhite = uFlatV.y >= 0.0 ? uFlatV.y : hsv.z * uInkWhite.w;
  float vEff = clamp(mA * vTeal + mB * vWhite + (1.0 - mA - mB) * hsv.z, 0.0, ${KIT_VAL_MAX.toFixed(4)});
  diffuseColor.rgb = fafiHsv2rgb(vec3(hEff, sEff, vEff));
  fafiKitMask = clamp(mA + mB, 0.0, 1.0); // D92: hand the KIT coverage to the emissive stage
}
`;

// D91/D92: the emissive channel is the game's HIGHLIGHT (controlled-player marker, keeper glow, contact
// telegraph), applied flat now that the baked emissiveMap is dropped (see animatedPlayers). It is confined
// to the JERSEY by the SAME two gates the diffuse recolor uses — `fafiKitMask` = (teal ink + white ink),
// already head-cut. D91 gated on height alone, which washed every below-the-chin pixel including BARE ARMS,
// HANDS and boots and flattened the kit's detail; colour-range gating is what makes it a kit highlight.
// Runs AFTER <emissivemap_fragment>, where totalEmissiveRadiance is finalised, and after the map stage that
// writes fafiKitMask (meshphysical_frag orders map_fragment before emissivemap_fragment).
const RECOLOR_GLSL_EMISSIVE = /* glsl */ `
#include <emissivemap_fragment>
totalEmissiveRadiance *= fafiKitMask;
`;

/** Patch a rig material to recolor the two baked inks per `kit` (see recolorKit for the exact maths).
 *  `chinLocalY` is the bind-pose local height (same space as the vertex `position` attribute) at/below
 *  which recoloration applies; everything above it (the face) keeps its baked texture. Per D91/D92 the
 *  emissive highlight rides the same gates — head-cut AND teal/white ink coverage — so a glow lights only
 *  jersey pixels. Omit `chinLocalY` (∞) to recolor/glow the whole mesh. */
export function applyKitPaint(mat: THREE.Material, kit: KitPaint, chinLocalY = 1e9): void {
  const [hA, sA] = slotHsvLinear(kit.teal.hex);
  const [hB, sB] = slotHsvLinear(kit.white.hex);
  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uInkTeal = { value: new THREE.Vector4(hA, sA, kit.teal.mix, kit.teal.valScale) };
    shader.uniforms.uInkWhite = { value: new THREE.Vector4(hB, sB, kit.white.mix, kit.white.valScale) };
    shader.uniforms.uFlatV = { value: new THREE.Vector2(kit.teal.flatV ?? -1, kit.white.flatV ?? -1) };
    shader.uniforms.uChinY = { value: chinLocalY };
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', RECOLOR_GLSL_VERT_COMMON)
      .replace('#include <begin_vertex>', RECOLOR_GLSL_VERT_BODY);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', RECOLOR_GLSL_COMMON)
      .replace('#include <map_fragment>', RECOLOR_GLSL_MAP)
      .replace('#include <emissivemap_fragment>', RECOLOR_GLSL_EMISSIVE);
  };
  mat.needsUpdate = true;
}
